import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { logStatsigEvent } from '../lib/statsig'

interface ChatWidgetContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const ChatWidgetContext = createContext<ChatWidgetContextValue | null>(null)

/**
 * Lifts ChatWidget.tsx's open/closed state out of that component so any
 * "chat with us" affordance elsewhere on the page (issue #49 - the phone-
 * number CTAs in Navbar/Footer/etc. now open chat instead of dialing) can
 * open the same floating panel, not just its own launcher button. Must wrap
 * everything inside BrowserRouter (needs useLocation for the analytics
 * event below) - mounted once in App.tsx, same level ChatWidget itself is
 * already mounted at.
 */
export function ChatWidgetProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  // Funnel top: how many visitors ever open chat at all, and from where -
  // fires only on the closed→open transition (a previous open→open call,
  // e.g. clicking a second "chat with us" link while already open, shouldn't
  // count again), regardless of which entry point triggered it.
  const open = useCallback(() => {
    setIsOpen(current => {
      if (!current) logStatsigEvent('chat_opened', undefined, { page: location.pathname })
      return true
    })
  }, [location.pathname])

  const close = useCallback(() => setIsOpen(false), [])

  const toggle = useCallback(() => {
    setIsOpen(current => {
      if (!current) logStatsigEvent('chat_opened', undefined, { page: location.pathname })
      return !current
    })
  }, [location.pathname])

  // Deep link (issue #79): ?chat=1 opens the panel on arrival. The SMS
  // auto-reply on the customer phone line points at /chat, which redirects
  // here - without this, a customer tapping that link landed on the homepage
  // and still had to find the floating launcher themselves, which is a poor
  // ask from a text message on a phone mid-emergency.
  //
  // Deliberately not routed through open() above: every existing caller
  // passes that straight to onClick (e.g. MobileCallBar's `onClick={openChat}`),
  // so giving it a `source` parameter would quietly start recording React's
  // MouseEvent as the source. Setting state here instead keeps open()'s
  // no-argument signature honest and leaves all six existing entry points
  // untouched.
  //
  // The ref makes this fire at most once per mount, so it survives StrictMode's
  // double-invoked effects and never re-opens a panel the customer has since
  // closed while the param is still sitting in the URL.
  const handledUrlOpen = useRef(false)
  useEffect(() => {
    if (handledUrlOpen.current) return
    if (new URLSearchParams(location.search).get('chat') !== '1') return
    handledUrlOpen.current = true
    setIsOpen(true)
    logStatsigEvent('chat_opened', undefined, { page: location.pathname, source: 'deeplink' })
  }, [location.search, location.pathname])

  return (
    <ChatWidgetContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </ChatWidgetContext.Provider>
  )
}

export function useChatWidget(): ChatWidgetContextValue {
  const ctx = useContext(ChatWidgetContext)
  if (!ctx) throw new Error('useChatWidget must be used within a ChatWidgetProvider')
  return ctx
}
